import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Header from '@/components/Header/Header';
import { ImportFileForm } from '@/components/Import/ImportFileForm';
import { ImportColumnMapper } from '@/components/Import/ImportColumnMapper';
import { useImportFileStore } from '@/stores/importFile';
import { ImportFileStepper } from '@/components/Import/ImportFileStepper';
import { ImportConfirm } from '@/components/Import/ImportConfirm';
import { CenteredColumn } from '@/components/CenteredColumn';
import { ImportValidate } from '@/components/Import/ImportValidate';
import { useValidateCollectionMemberImport } from '@/api/trafikportalen';
import { useGetStudentCollection } from '@/hooks/useGetStudentCollection';
import {
  sanitizeImportRowsForValidation,
  transformToCollectionImportRows,
} from '@/helpers/transformImportData';
import { toast } from '@/hooks/useToast';
import { Loading } from '@/components/Loading';
import { WarningDialog } from '@/components/WarningDialog';
import type { SearchFilter } from '@/types/filterSearch';
import type { ImportSearchParams } from '@/components/Import/importSearchSchema';
import { StudentType } from '@/types/students';
import { useHalfProductAvailability } from '@/hooks/useHalfProductAvailability';
import { useRememberedImportMappings } from '@/hooks/useRememberedImportMappings';

interface StudentImportProps {
  formType: StudentType;
  tenantId: string;
  searchParams: ImportSearchParams & SearchFilter;
}

export const StudentImport = ({
  formType,
  tenantId,
  searchParams,
}: StudentImportProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [stepAnnouncement, setStepAnnouncement] = useState('');
  const [pendingStep, setPendingStep] = useState<number | null>(null);
  const prevStepRef = useRef(1);

  const {
    reset,
    import: fileData,
    setImportData,
    setValidationResult,
    setValidating,
    editedRows,
    clearEditedRows,
  } = useImportFileStore();

  const { formatMessage } = useIntl();
  const isHalfProductAvailable = useHalfProductAvailability();
  const disableHalfProduct = !isHalfProductAvailable(formType);
  const { rememberMappings } = useRememberedImportMappings(formType);

  const {
    collectionId,
    students,
    isLoading: isLoadingCollection,
  } = useGetStudentCollection(formType);

  const validateImport = useValidateCollectionMemberImport();

  const hasEdits = editedRows.size > 0;

  const onChangeStep = useCallback(
    async (newStep: number) => {
      if (currentStep === 3 && newStep < 3 && hasEdits) {
        setPendingStep(newStep);
        return;
      }

      if (newStep === 3 && currentStep === 2) {
        if (!collectionId) {
          toast.error({
            title: formatMessage({
              defaultMessage: 'Kunde inte hitta samlingen för import',
            }),
          });
          return;
        }

        const headers = fileData.file?.importHeaders;
        const rows = fileData.file?.importRows;
        const mappedColumns = fileData.mappedColumns;

        if (!headers || !rows || !mappedColumns) {
          toast.error({
            title: formatMessage({
              defaultMessage: 'Saknar data för validering',
            }),
          });
          return;
        }

        rememberMappings(mappedColumns);
        setValidating(true);

        try {
          const importRows = transformToCollectionImportRows(
            headers,
            rows,
            mappedColumns,
            students ?? [],
            { disableHalfProduct },
          );

          setImportData(importRows);

          const result = await validateImport.mutateAsync({
            collectionID: collectionId,
            data: {
              rows: sanitizeImportRowsForValidation(importRows),
              strategy: 'replace',
            },
          });

          setValidationResult(result);
          setCurrentStep(newStep);
        } catch (error) {
          toast.error({
            title: formatMessage({
              defaultMessage: 'Ett fel uppstod vid validering',
            }),
          });
          console.error('Validation error:', error);
        } finally {
          setValidating(false);
        }
      } else {
        setCurrentStep(newStep);
      }
    },
    [
      currentStep,
      hasEdits,
      collectionId,
      fileData,
      formatMessage,
      rememberMappings,
      setImportData,
      setValidating,
      setValidationResult,
      validateImport,
      disableHalfProduct,
      students,
    ],
  );

  const handleConfirmGoBack = useCallback(() => {
    if (pendingStep == null) return;
    clearEditedRows();
    setCurrentStep(pendingStep);
    setPendingStep(null);
  }, [pendingStep, clearEditedRows]);

  const handleCancelGoBack = useCallback(() => {
    setPendingStep(null);
  }, []);

  const steps = useMemo(() => {
    return [
      {
        step: 1,
        title: formatMessage({ defaultMessage: '1. Ladda upp fil' }),
        view: <ImportFileForm type={formType} />,
      },
      {
        step: 2,
        title: formatMessage({ defaultMessage: '2. Matcha fält' }),
        view: <ImportColumnMapper type={formType} />,
      },
      {
        step: 3,
        title: formatMessage({ defaultMessage: '3. Valideringsresultat' }),
        view: <ImportValidate searchParams={searchParams} type={formType} />,
      },
      {
        step: 4,
        title: formatMessage({ defaultMessage: '4. Slutför import' }),
        view: collectionId ? (
          <ImportConfirm
            collectionId={collectionId}
            tenantId={tenantId}
            formType={formType}
          />
        ) : null,
      },
    ];
  }, [formatMessage, formType, collectionId, tenantId, searchParams]);

  const isValidating = fileData.status?.isValidating ?? false;

  const disableNextStepper = useMemo(() => {
    if (currentStep === 1) {
      return !fileData.file?.importColumns;
    }
    if (currentStep === 2) {
      return (
        !fileData.status?.isAllColumnsValidated ||
        isLoadingCollection ||
        isValidating
      );
    }
    return false;
  }, [
    currentStep,
    fileData.file?.importColumns,
    fileData.status?.isAllColumnsValidated,
    isLoadingCollection,
    isValidating,
  ]);

  const renderCurrentStep = useCallback(() => {
    if (isValidating) {
      return (
        <div className="flex flex-col items-center gap-4 py-8">
          <Loading />
          <span className="text-text-700">
            <FormattedMessage defaultMessage="Validerar importdata..." />
          </span>
        </div>
      );
    }
    return steps.find((step) => step.step === currentStep)?.view;
  }, [currentStep, steps, isValidating]);

  useEffect(() => {
    if (currentStep !== prevStepRef.current) {
      const stepTitle = steps.find((s) => s.step === currentStep)?.title ?? '';
      setStepAnnouncement(
        formatMessage(
          { defaultMessage: 'Steg {step} av {total}: {title}' },
          { step: currentStep, total: steps.length, title: stepTitle },
        ),
      );
      prevStepRef.current = currentStep;
    }
  }, [currentStep, steps, formatMessage]);

  useEffect(() => {
    if (isValidating) {
      setStepAnnouncement(
        formatMessage({ defaultMessage: 'Validerar importdata…' }),
      );
    }
  }, [isValidating, formatMessage]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const isValidateStep = currentStep === 3;

  return (
    <>
      <Header
        title={formatMessage({ defaultMessage: 'Importera' })}
        description={formatMessage({ defaultMessage: 'Importera från fil' })}
      />
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {stepAnnouncement}
      </div>
      <div className="flex size-full flex-col overflow-auto">
        <CenteredColumn>
          <ImportFileStepper
            steps={steps}
            currentStep={currentStep}
            nextIsDisabled={disableNextStepper}
            onChange={onChangeStep}
          />
        </CenteredColumn>
        {isValidateStep ? (
          <div className="flex min-h-0 grow">{renderCurrentStep()}</div>
        ) : (
          <CenteredColumn className="grow">
            {renderCurrentStep()}
          </CenteredColumn>
        )}
      </div>
      <WarningDialog
        open={pendingStep != null}
        onOpenChange={() => setPendingStep(null)}
        title={formatMessage({
          defaultMessage: 'Ändringar kommer att försvinna',
        })}
        content={
          <p>
            <FormattedMessage
              defaultMessage="Du har redigerat {count, plural, one {# rad} other {# rader}} i valideringssteget. Dessa ändringar försvinner om du går tillbaka."
              values={{ count: editedRows.size }}
            />
          </p>
        }
        onCancel={handleCancelGoBack}
        onConfirm={handleConfirmGoBack}
        buttonText={formatMessage({ defaultMessage: 'Gå tillbaka' })}
      />
    </>
  );
};
